import Button from '@rio-cloud/rio-uikit/Button';
import Checkbox from '@rio-cloud/rio-uikit/Checkbox';
import ConfirmationDialog from '@rio-cloud/rio-uikit/ConfirmationDialog';
import { FormattedMessage } from 'react-intl';
import { useAppDispatch } from '../../../../configuration/setup/hooks';
import { updateAsset } from '../../actions/assets/Assets.actions';
import { BulkActiveContractsWarning } from './BulkActiveContractsWarning';
import { type Asset, AssetStatus } from './types';
import { useIsLatamTenant, useIsTestTenant } from '../../../../configuration/login/loginSlice';
import { useBulkGetActiveContractsQuery } from '../../../services/contractsApi';
import { useCheckActiveContractsBulkQuery } from '../../../services/latamContractsApi';
import { useState } from 'react';

export interface ArchiveAssetConfirmationDialogProps {
    show: boolean;
    assets: Asset[];
    hideBulkArchiveAssetDialog: () => void;
}

export const BulkArchiveAssetConfirmationDialog = (props: ArchiveAssetConfirmationDialogProps) => {
    const assetIds = props.assets.map(asset => asset.id);
    const dispatch = useAppDispatch();
    const isLatamTenant = useIsLatamTenant() || false;
    const isTestTenant = useIsTestTenant() || false;
    const { data: activeContracts } = useBulkGetActiveContractsQuery(assetIds, { skip: isLatamTenant });
    const { data: activeContractsLatam } = useCheckActiveContractsBulkQuery(assetIds, { skip: !isLatamTenant });
    const [checkBoxChecked, setCheckBox] = useState<boolean>(false);
    const [expanded, setExpanded] = useState(false);
    const visibleItems = expanded ? props.assets : props.assets.slice(0, 5);

    const bookedServiceIds = new Set([
        ...(activeContracts ?? []).map(c => c.id),
        ...(activeContractsLatam ?? []).map(c => c.id),
    ]);

    const allAssetsHaveBookedServices = assetIds.every(assetId => bookedServiceIds.has(assetId));
    const someAssetHaveBookedServices = assetIds.some(assetId => bookedServiceIds.has(assetId));
    const isContactSupportButtonShown = someAssetHaveBookedServices && isLatamTenant && !isTestTenant;
    const disableConfirm =
        isLatamTenant &&
        !isTestTenant &&
        (allAssetsHaveBookedServices || (someAssetHaveBookedServices && !checkBoxChecked));

    const archiveAssets = () => {
        for (const originalAsset of props.assets.filter(asset => !bookedServiceIds.has(asset.id))) {
            const updatedAsset: Asset = { ...originalAsset, status: AssetStatus.archived };
            dispatch(updateAsset(originalAsset, updatedAsset));
        }
        props.hideBulkArchiveAssetDialog();
    };

    const archiveAssetModalContent = (
        <div>
            <div className='text-size-h3 margin-bottom-20'>
                <FormattedMessage id={'assets.assets.asset.archive-confirmation-dialog.bulk-confirmation-question'} />
            </div>
            <div className='text-size-16 margin-bottom-15'>
                <ul>
                    {visibleItems.map(asset => (
                        <li key={asset.id}>
                            <strong>{asset.name}</strong>
                        </li>
                    ))}
                </ul>
            </div>
            {!expanded && props.assets.length > 5 && (
                <Button bsStyle={Button.MUTED} className='margin-bottom-20' onClick={() => setExpanded(true)}>
                    <FormattedMessage
                        id={'assets.assets.asset.archive-confirmation-dialog.bulk-confirmation-show-all'}
                    />{' '}
                    ({props.assets.length})
                </Button>
            )}
            <div className='alert alert-warning display-flex flex-column gap-15'>
                <div className='display-flex gap-10'>
                    <span className='text-color-warning text-size-h4 rioglyph rioglyph-warning-sign' />
                    <div>
                        <strong className='text-size-16'>
                            <FormattedMessage
                                id={'assets.assets.asset.archive-confirmation-dialog.content.remove-data-sources.title'}
                            />
                        </strong>
                        <div>
                            <FormattedMessage
                                id={'assets.assets.asset.archive-confirmation-dialog.content.remove-data-sources.hint'}
                            />
                        </div>
                    </div>
                </div>

                {isLatamTenant ? (
                    ''
                ) : (
                    <div className='display-flex gap-10'>
                        <span className='text-color-warning text-size-h4 rioglyph rioglyph-warning-sign' />
                        <div>
                            <strong className='text-size-16'>
                                <FormattedMessage
                                    id={'assets.assets.asset.delete-confirmation-dialog.content.booked-services.title'}
                                />
                            </strong>
                            <div>
                                <FormattedMessage
                                    id={'assets.assets.asset.delete-confirmation-dialog.content.booked-services.hint'}
                                />
                            </div>
                        </div>
                    </div>
                )}

                <BulkActiveContractsWarning assets={props.assets} deletionMode={false} />

                {isContactSupportButtonShown && (
                    <div style={{ display: 'flex', justifyContent: 'right' }}>
                        <button
                            onClick={() =>
                                window.open(
                                    encodeURI(
                                        `https://contact-form.rio.cloud/contact?opener=archive assets ${assetIds}`
                                    ),
                                    '_blank'
                                )
                            }
                            type='button'
                            className='btn btn-primary'
                        >
                            <FormattedMessage
                                id={'assets.assets.asset.archive-confirmation-dialog.button.contact-support'}
                            />
                        </button>
                    </div>
                )}

                {isContactSupportButtonShown && !allAssetsHaveBookedServices ? (
                    <Checkbox
                        data-testid='skip-assets-checkbox'
                        checked={checkBoxChecked}
                        onClick={() => setCheckBox(!checkBoxChecked)}
                    >
                        <FormattedMessage
                            id={'assets.assets.asset.archive-confirmation-dialog.bulk-confirmation-skip'}
                        />
                    </Checkbox>
                ) : (
                    ''
                )}
            </div>
            <div className='padding-top-20 children-last-margin-bottom-0'>
                <div className='text-bold margin-bottom-5'>
                    <FormattedMessage id={'assets.assets.asset.archive-confirmation-dialog.content.reason.question'} />
                </div>
                <p>
                    <FormattedMessage
                        id={'assets.assets.asset.archive-confirmation-dialog.content.reason.explanation'}
                    />
                </p>
            </div>
        </div>
    );

    return (
        <ConfirmationDialog
            show={props.show}
            bsSize={'sm'}
            content={archiveAssetModalContent}
            onClickConfirm={archiveAssets}
            onClickCancel={props.hideBulkArchiveAssetDialog}
            cancelButtonText={<FormattedMessage id={'assets.assets.asset.archive-confirmation-dialog.button.cancel'} />}
            confirmButtonText={
                <FormattedMessage id={'assets.assets.asset.archive-confirmation-dialog.button.confirm'} />
            }
            disableConfirm={disableConfirm}
            useOverflow={true}
        />
    );
};
